import { Suspense } from 'react';
import { BrowserRouter as Router, useRoutes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { routes } from './routes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WhatsAppSyncProvider, useWhatsAppSync } from './contexts/WhatsAppSyncContext';
import { NChatProvider } from './contexts/NChatContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DebugBanner } from './components/DebugBanner';
import { MohapRouteGate } from './components/MohapRouteGate';
import { EliasRouteGate } from './components/EliasRouteGate';
import { WhatsAppChatSelectorModal } from './components/n71/integrations/WhatsAppChatSelectorModal';
import { WhatsAppChatPickerBanner } from './components/n71/integrations/WhatsAppChatPickerBanner';

function AppRoutes() {
  const element = useRoutes(routes);
  return element;
}

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <span 
        className="text-sm font-medium text-foreground/30 tracking-wider animate-pulse"
      >
        N71
      </span>
    </div>
  );
}

/**
 * Service Unavailable Screen
 * Shown when Supabase auth is down (circuit breaker open)
 */
function ServiceUnavailable() {
  const { retryConnection } = useAuth();
  
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center max-w-md p-8">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-foreground mb-2">
          Service Temporarily Unavailable
        </h1>
        <p className="text-foreground/60 mb-6">
          We're having trouble connecting to our servers. This is usually temporary.
          Please try again in a moment.
        </p>
        <button
          onClick={retryConnection}
          className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
        >
          Try Again
        </button>
        <p className="text-xs text-foreground/40 mt-4">
          If this persists, check{' '}
          <a 
            href="https://status.supabase.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:text-foreground/60"
          >
            status.supabase.com
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Auth Guard - shows loading, service unavailable, or children
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isLoading, isServiceUnavailable } = useAuth();
  
  if (isServiceUnavailable) {
    return <ServiceUnavailable />;
  }
  
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  return <>{children}</>;
}

/**
 * Global WhatsApp Chat Selector Modal
 * Rendered at app level so it works from anywhere (onboarding, main page, etc.)
 */
function GlobalWhatsAppChatSelector() {
  const { chatSelectorOpen, activeSyncSessionId, pendingSyncs, closeChatSelector, removePendingSync } = useWhatsAppSync();

  // Find the workspace ID for the active sync
  const activeSync = pendingSyncs.find(s => s.sessionId === activeSyncSessionId);

  if (!chatSelectorOpen || !activeSyncSessionId || !activeSync) {
    return null;
  }

  const handleClose = () => {
    // Remove from pending syncs when modal closes (either completed or skipped)
    removePendingSync(activeSyncSessionId);
    closeChatSelector();
  };

  return (
    <WhatsAppChatSelectorModal
      isOpen={chatSelectorOpen}
      onClose={handleClose}
      sessionId={activeSyncSessionId}
      workspaceId={activeSync.workspaceId}
    />
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AuthGuard>
            <WorkspaceProvider>
              <EntitlementsProvider>
                <WhatsAppSyncProvider>
                  {/* NChatProvider hoisted to App level (May 2026): the
                      chat WebSocket and in-flight agent state must
                      survive ALL in-app navigation, including leaving
                      the chat surface and returning. Previously, each
                      product (N71, Ministry) wrapped its own routes
                      with NChatProvider, so /mohap → /context → /mohap
                      would unmount the provider, kill the socket, and
                      orphan the agent run. Mounting once at the app
                      root keeps one socket alive per session. */}
                  <NChatProvider>
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        {/* Auto-routes mohap-only users to /mohap on every
                            non-allowed nav. Renders nothing — pure side effect. */}
                        <MohapRouteGate />
                        {/* Same pattern for the Elias edition. Both gates run;
                            mutually exclusive in practice because a workspace
                            carries one edition value. */}
                        <EliasRouteGate />
                        <AppRoutes />
                      </Suspense>
                    {/* Global chat-picker banner: surfaces the chat selection
                        step whenever a user has a WA session with chats ready
                        but hasn't completed the picker yet. Self-hides on
                        /onboarding/* routes and when dismissed/opted-out. */}
                    <WhatsAppChatPickerBanner />
                    <GlobalWhatsAppChatSelector />
                    <Toaster
                      position="bottom-right"
                      toastOptions={{
                        style: {
                          background: 'hsl(var(--background))',
                          color: 'hsl(var(--foreground))',
                          border: '1px solid hsl(var(--border))',
                        },
                        className: 'shadow-lg',
                      }}
                    />
                    <DebugBanner />
                    {/* Release-notes / what's-new lives in the authenticated
                        dashboard shell (DashboardShell → UpdatesModal), not at
                        the app root — so it never surfaces on the public
                        landing or auth pages. */}
                    </ErrorBoundary>
                  </NChatProvider>
                </WhatsAppSyncProvider>
              </EntitlementsProvider>
            </WorkspaceProvider>
          </AuthGuard>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;